import React, { useEffect, useState } from 'react';
import { getCycleNotes, addCycleNote } from '../services/kraCycleService';
import useUIStore from '../store/uiStore';

/**
 * General-purpose observation/comment thread on one MonthlyKRACycle -
 * distinct from the per-item Self/HOD/External comments already shown in
 * the scoring table. Anyone who can view the cycle (self, that employee's
 * HOD, HR, CEO, System_Admin) can read and post here; a note can be marked
 * private to hide it from the employee themself (the checkbox is hidden
 * entirely when the viewer IS that employee, since it wouldn't do anything).
 *
 * Reused by MyKRACycleDetailPage and TeamReviewDetailPage - kept as a
 * standalone component with its own local state rather than folded into
 * kraCycleStore, since notes are a self-contained side panel, not part of
 * the scoring flow those pages/store already manage.
 */
const KRACycleNotes = ({ cycleId, isOwnCycle }) => {
  const showToast = useUIStore((s) => s.showToast);
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [isPosting, setIsPosting] = useState(false);

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await getCycleNotes(cycleId);
      setNotes(res.data.notes || []);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load notes', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId]);

  const handlePost = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setIsPosting(true);
    try {
      const res = await addCycleNote(cycleId, draft.trim(), isPrivate);
      setNotes((prev) => [...prev, res.data.note]);
      setDraft('');
      setIsPrivate(false);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to post note', 'error');
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <div className="glass-card overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-200">
        <h2 className="font-semibold text-gray-900">Notes &amp; Observations</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          General comments about this month — separate from scoring a specific KRA line item above.
        </p>
      </div>

      <div className="px-5 py-4 space-y-3 max-h-80 overflow-y-auto">
        {isLoading ? (
          <p className="text-xs text-gray-400">Loading...</p>
        ) : notes.length === 0 ? (
          <p className="text-xs text-gray-400">No notes yet.</p>
        ) : (
          notes.map((n) => (
            <div key={n.id} className={`text-sm p-3 rounded ${n.is_private ? 'bg-amber-50 border border-amber-100' : 'bg-gray-50'}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-semibold text-gray-800 text-xs">{n.author_name}</span>
                <span className="text-xs text-gray-400">
                  {new Date(n.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  {n.is_private && <span className="ml-2 text-amber-700 font-semibold">Private</span>}
                </span>
              </div>
              <p className="text-gray-700 whitespace-pre-wrap">{n.note}</p>
            </div>
          ))
        )}
      </div>

      <form onSubmit={handlePost} className="px-5 py-4 border-t border-gray-200 space-y-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a note or observation..."
          rows={2}
          className="w-full text-sm border border-gray-300 rounded px-3 py-2"
        />
        <div className="flex items-center justify-between">
          {!isOwnCycle && (
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />
              Private (hidden from the employee)
            </label>
          )}
          <button
            type="submit"
            disabled={isPosting || !draft.trim()}
            className="ml-auto px-4 py-1.5 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
          >
            {isPosting ? 'Posting...' : 'Post Note'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default KRACycleNotes;
